import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 border-b ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-line"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-baseline gap-2 group"
          aria-label="LEDOS Enterprise Solutions home"
        >
          <img
            src="/logo-mark.png"
            alt=""
            className="h-9 w-auto"
            data-testid="nav-logo-mark"
          />
          <span className="font-display text-2xl font-black tracking-tight text-white group-hover:text-signal transition-colors duration-300">
            LEDOS
          </span>
          <span className="hidden sm:block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500">
            Enterprise Solutions
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                  isActive ? "text-signal" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-discuss-project"
            className="group inline-flex items-center gap-2 bg-signal text-black font-mono text-xs font-bold tracking-[0.15em] uppercase px-5 py-3 hover:-translate-y-0.5 transition-transform duration-300"
          >
            Discuss Your Project
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-line"
            aria-label="Mobile"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display text-3xl font-bold tracking-tight ${
                      isActive ? "text-signal" : "text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="nav-mobile-cta"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-signal text-black font-mono text-xs font-bold tracking-[0.15em] uppercase px-5 py-4"
              >
                Discuss Your Project
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
